import { decodeRef, formatDate, type ActivityEntry } from "@love-cpmd/core";
import type { World } from "./queries";

export interface ActivityView {
  /** Sentence without the actor, e.g. "moved Budi's schedule from 27 Oct to 10 Nov". */
  text: string;
  detail?: string;
  /** Where tapping the entry leads. */
  link?: { kind: "session"; id: string } | { kind: "path"; to: string };
}

const d = (iso: unknown) => (typeof iso === "string" && iso ? formatDate(iso, "dayMonth") : "");

export function describeActivity(entry: ActivityEntry, world: World): ActivityView {
  const data = entry.data;
  const ref = (key: string) => decodeRef(data[key] as string | undefined);
  const name = (key: string) => world.name(ref(key), "short");
  /** "their" when the actor owns it, otherwise "Budi's". */
  const whose = (key: string) => {
    const r = ref(key);
    if (r?.kind === "member" && r.id === entry.actorId) return "their";
    return `${world.name(r, "short")}'s`;
  };
  const sessionLink = { kind: "session" as const, id: entry.target.id };

  switch (entry.action) {
    case "CREATE_SCHEDULE": {
      const r = ref("presenter");
      const self = r?.kind === "member" && r.id === entry.actorId;
      return { text: self ? `scheduled their own session for ${d(data.date)}` : `scheduled ${name("presenter")} to present on ${d(data.date)}`, link: sessionLink };
    }
    case "FILL_FROM_ROTATION":
      return {
        text: `filled ${data.count} session${data.count === 1 ? "" : "s"} from rotation cycle ${data.cycle}`,
        detail: data.from ? `${d(data.from)} – ${d(data.to)}` : undefined,
        link: { kind: "path", to: "/schedule/list" },
      };
    case "UPDATE_TOPIC":
      return { text: `updated the topic for ${whose("owner")} session on ${d(data.date)}`, link: sessionLink };
    case "RESCHEDULE":
      return {
        text: `moved ${whose("owner")} schedule from ${d(data.from)} to ${d(data.to)}`,
        detail: data.reason ? `“${data.reason}”` : undefined,
        link: sessionLink,
      };
    case "SWAP": {
      const a = ref("a");
      const actorIsA = a?.kind === "member" && a.id === entry.actorId;
      const text = actorIsA
        ? `swapped their ${d(data.aFrom)} slot with ${name("b")}'s ${d(data.bFrom)} slot`
        : `swapped ${name("a")}'s ${d(data.aFrom)} slot with ${name("b")}'s ${d(data.bFrom)} slot`;
      return { text, detail: data.reason ? `“${data.reason}”` : undefined, link: { kind: "session", id: String(data.aSession ?? entry.target.id) } };
    }
    case "MARK_DONE":
      return {
        text: `marked ${whose("presenter")} session on ${d(data.date)} as done`,
        detail: data.replacement ? "Presented by a replacement" : undefined,
        link: sessionLink,
      };
    case "REPLACEMENT":
      return { text: `set ${name("to")} to present in place of ${name("from")} on ${d(data.date)}`, link: sessionLink };
    case "CANCEL":
      return {
        text: `cancelled ${whose("presenter")} session on ${d(data.date)}`,
        detail: data.reason ? `Reason: ${data.reason}` : undefined,
        link: sessionLink,
      };
    case "SPONTANEOUS":
      return { text: `logged extra sharing by ${name("presenter")} on ${d(data.date)}`, detail: data.topic ? `“${data.topic}”` : undefined, link: sessionLink };
    case "CREATE_SUMMARY":
      return { text: `published the summary for “${data.topic}”`, link: { kind: "path", to: `/summary/${data.sessionId}` } };
    case "UPDATE_SUMMARY":
      return { text: `edited the summary for “${data.topic}”`, link: { kind: "path", to: `/summary/${data.sessionId}` } };
    case "GENERATE_ROTATION":
      return {
        text: data.reshuffle ? `reshuffled the draft order for rotation cycle ${data.cycle}` : `generated a random order for rotation cycle ${data.cycle}`,
        link: { kind: "path", to: "/schedule/rotation" },
      };
    case "REORDER_ROTATION":
      return { text: `changed the order of rotation cycle ${data.cycle}`, link: { kind: "path", to: "/schedule/rotation" } };
    case "LOCK_ROTATION":
      return { text: `locked rotation cycle ${data.cycle}${data.members ? ` for ${data.members} members` : ""}`, link: { kind: "path", to: "/schedule/rotation" } };
    case "UNLOCK_ROTATION":
      return { text: `unlocked rotation cycle ${data.cycle}`, link: { kind: "path", to: "/schedule/rotation" } };
    case "MEMBER_ADD":
      return { text: `added ${world.memberName(String(data.member))} (${data.memberId})`, link: { kind: "path", to: "/admin/members" } };
    case "MEMBER_UPDATE":
      return { text: `updated ${world.memberName(String(data.member), "short")}'s account`, link: { kind: "path", to: "/admin/members" } };
    case "PASSWORD_RESET":
      return { text: `reset ${world.memberName(String(data.member), "short")}'s password`, link: { kind: "path", to: "/admin/members" } };
    case "CATEGORY_ADD":
      return { text: `added the category “${data.name}”`, link: { kind: "path", to: "/admin/categories" } };
    case "CATEGORY_UPDATE":
      return { text: `updated the category “${data.name}”`, link: { kind: "path", to: "/admin/categories" } };
    case "SETTINGS_UPDATE":
      return { text: "updated system settings", link: { kind: "path", to: "/admin/settings" } };
  }
}
